import ApplicationController from './application_controller';
import axios from 'axios';
import autocomplete from 'autocomplete.js';

export default class extends ApplicationController {
  static targets = [
    "map", "field", "latitude", "longitude", 'city', 'stateSelect',
    'newCityPanel', 'newCityHeadline', 'candidatesTitle', 'candidateList', 'confirmCheck', 'confirmLabel'
  ];
  static values = { lat: Number, long: Number }

  connect() {
    if (typeof (google) != "undefined") {
      this.initMap()
    }

    if (this.hasCityTarget) {
      this.addAutocomplete(this.cityTarget)
    } else {
      console.info('City autocomplete target not found.')
    }
  }

  initMap() {
    const latLng = {
      lat: this.latValue || 22.77,
      lng: this.longValue || -101.17,
    };

    this.map = new window.google.maps.Map(this.mapTarget, {
      center: new window.google.maps.LatLng(latLng),
      zoom: (!this.latValue ? 5 : 17)
    })

    if (this.hasFieldTarget) {
      this.autocomplete = new window.google.maps.places.Autocomplete(this.fieldTarget)
      this.autocomplete.bindTo('bounds', this.map);
      this.autocomplete.setFields(['address_components', 'geometry', 'icon', 'name'])
      this.autocomplete.addListener('place_changed', this.placeChanged.bind(this))
    }

    this.marker = new window.google.maps.Marker({
      map: this.map,
      position: latLng,
      draggable: true,
    })
  }

  placeChanged() {
    let place = this.autocomplete.getPlace()

    if (!place.geometry) {
      window.alert("No hay resultados que coincidan")
      return
    }

    if (place.geometry.viewport) {
      this.map.fitBounds(place.geometry.viewport)
    } else {
      this.map.setCenter(place.geometry.location)
      this.map.setZoom(17)
    }

    this.marker.setPosition(place.geometry.location)
    this.marker.setVisible(true)

    this.latitudeTarget.value = place.geometry.location.lat()
    this.longitudeTarget.value = place.geometry.location.lng()
  }

  keydown(event) {
    if (event.key == "Enter") {
      event.preventDefault();
    }
  }

  // The city input is a plain named field: whatever the operator typed is what
  // gets submitted. Selecting a suggestion just fills it in (and its state), it
  // is not a precondition for submitting — a city that does not exist yet is
  // created server-side once the operator confirms it.
  addAutocomplete(inputTarget) {
    const ac = autocomplete(inputTarget, { hint: false }, [
      {
        source: this.source(),
        debounce: 200,
        templates: {
          suggestion: function (suggestion) {
            return suggestion.state
              ? `${suggestion.name} — ${suggestion.state}`
              : suggestion.name || 'Invalid suggestion data';
          },
        },
      },
    ]).on('autocomplete:selected', (event, suggestion, dataset, context) => {
      ac.autocomplete.setVal(suggestion.name);
      if (this.hasStateSelectTarget) {
        // Sync the state to the picked city. The payload carries state_id now, so
        // the select is filled rather than cleared; leaving the pre-filled state
        // on edit would attach the picked city to the old state. An empty value
        // still lets Terminal#set_state infer the state server-side.
        this.stateSelectTarget.value = suggestion.state_id || '';
      }
      // A picked suggestion is by definition a city that already exists: drop the
      // panel, and the in-flight check for the half-typed name along with it.
      this.invalidateCityCheck();
      this.hideNewCityPanel();
    });

    // Kept as handles rather than inline arrows so disconnect() can take them
    // back off again.
    this.cityInputHandler = () => this.scheduleCityCheck();
    this.cityBlurHandler = () => this.scheduleCityCheck(0);
    this.cityInput = inputTarget;
    inputTarget.addEventListener('input', this.cityInputHandler);
    inputTarget.addEventListener('blur', this.cityBlurHandler);
  }

  // Turbolinks re-connects a controller on every cached-page restore, so what
  // connect() wired has to be undone here: without it the pending debounce fires
  // against a panel that is no longer on screen, and each restore leaves another
  // copy of the two listeners behind.
  disconnect() {
    this.invalidateCityCheck();

    if (this.cityInput) {
      this.cityInput.removeEventListener('input', this.cityInputHandler);
      this.cityInput.removeEventListener('blur', this.cityBlurHandler);
      this.cityInput = null;
    }
  }

  scheduleCityCheck(delay = 350) {
    clearTimeout(this.cityCheckTimer);
    this.cityCheckTimer = setTimeout(() => this.checkCity(), delay);
  }

  // Drops the queued check AND any answer already on the wire. Bumping the
  // sequence is the only thing that makes checkCity's guard reject a reply for
  // text the operator has moved on from: the guard rejects replies superseded by
  // a LATER check, so without this a suggestion picked while a request is open
  // would be followed by that reply re-opening the new-city panel over a pick
  // that is by definition an existing city.
  //
  // @return {number} the sequence a check starting now should carry
  invalidateCityCheck() {
    clearTimeout(this.cityCheckTimer);
    this.cityCheckSeq = (this.cityCheckSeq || 0) + 1;

    return this.cityCheckSeq;
  }

  // Asks the same endpoint the autocomplete uses whether the typed name already
  // exists, and reveals the create-this-city prompt when it does not. This is a
  // hint, not a decision: the server re-resolves the name and re-checks the
  // confirmation on submit, so a stale, spoofed or skipped panel cannot create
  // anything on its own (see CityResolver).
  checkCity() {
    if (!this.hasCityTarget || !this.hasNewCityPanelTarget) return;

    // Only the newest answer may touch the panel: a slow reply for an earlier
    // keystroke would otherwise describe a name the operator has moved on from.
    // Claimed before the blank check so a cleared field also silences whatever
    // was already in flight.
    const seq = this.invalidateCityCheck();

    const typed = this.cityTarget.value.trim();
    if (typed === '') {
      this.hideNewCityPanel();
      return;
    }

    axios.get('/cities/search', { params: { q: typed } }).then((response) => {
      if (seq !== this.cityCheckSeq) return;

      const results = response.data || [];
      const normalized = this.normalizeCity(typed);
      const exact = results.find((city) => this.normalizeCity(city.name) === normalized);

      if (exact) {
        this.hideNewCityPanel();
        // Only when the operator has not chosen one: typing a full existing name
        // without touching the list should still fill the state, but an explicit
        // choice is theirs to keep.
        if (this.hasStateSelectTarget && this.stateSelectTarget.value === '') {
          this.stateSelectTarget.value = exact.state_id || '';
        }
      } else {
        this.showNewCityPanel(typed, results);
      }
    }).catch(() => {
      // A failed hint is nothing the operator can act on, and the server asks the
      // same question again on submit. Leave the panel as it is.
    });
  }

  // Mirrors CityResolver.normalize (transliterate, strip, downcase) so the panel
  // appears on exactly the names the server would call new.
  //
  // Not the same algorithm, only the same answer on the alphabets this admin sees:
  // NFD + combining-mark stripping handles every Spanish and Portuguese diacritic,
  // but it cannot reproduce a transliteration that changes length the way
  // ActiveSupport does (German ß → ss, Polish ł → l). On such a name this panel
  // would offer to create a city the server then resolves to the existing row — a
  // wrong hint, never a wrong write: CityResolver re-resolves on submit and its
  // answer is the one that decides.
  normalizeCity(value) {
    return (value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
  }

  showNewCityPanel(typed, candidates) {
    const panel = this.newCityPanelTarget;

    if (this.hasConfirmCheckTarget) {
      // The confirmation belongs to the name it was given for: any edit drops it,
      // which is also what the server does with a stale value.
      if (this.confirmCheckTarget.value !== typed) this.confirmCheckTarget.checked = false;
      this.confirmCheckTarget.value = typed;
    }
    if (this.hasNewCityHeadlineTarget) {
      this.newCityHeadlineTarget.textContent = this.fillCityTemplate(panel.dataset.headlineTemplate, typed);
    }
    if (this.hasConfirmLabelTarget) {
      this.confirmLabelTarget.textContent = this.fillCityTemplate(panel.dataset.confirmTemplate, typed);
    }
    if (this.hasCandidateListTarget) {
      this.candidateListTarget.innerHTML = '';
      candidates.forEach((city) => {
        const item = document.createElement('li');
        item.textContent = city.state ? `${city.name} — ${city.state}` : city.name;
        this.candidateListTarget.appendChild(item);
      });
    }
    if (this.hasCandidatesTitleTarget) {
      this.candidatesTitleTarget.classList.toggle('d-none', candidates.length === 0);
    }

    panel.classList.remove('d-none');
  }

  hideNewCityPanel() {
    if (!this.hasNewCityPanelTarget) return;

    if (this.hasConfirmCheckTarget) this.confirmCheckTarget.checked = false;
    this.newCityPanelTarget.classList.add('d-none');
  }

  fillCityTemplate(template, city) {
    return (template || '').replace('%{city}', city);
  }

  // /cities/search, not /routes/search_city: the latter goes through
  // Admin::RouteSearch#autocomplete_results, which queries TransporterTerminal
  // and so can only ever offer a city that already has a provider terminal.
  source() {
    const url = '/cities/search';
    return (q, callback) => {
      axios.get(url, { params: { q } }).then((response) => {
        callback(response.data);
      });
    };
  }
}
